import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faUserPlus,
  faClockRotateLeft,
  faEye,
  faClipboardCheck,
  faListCheck,
  faUserGear,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import {
  inspectionService,
  WORK_QUEUE_STAGE_LABELS,
  type Inspection,
} from '@/services/inspection.service';
import { dashboardService } from '@/services/dashboard.service';
import { departmentService } from '@/services/department.service';
import { OwnershipHistoryPanel } from '@/features/inspection/components/ownership/OwnershipHistoryPanel';
import {
  ForceTransferDialog,
  type ForceTransferDialogMode,
} from '@/features/inspection/components/ownership/ForceTransferDialog';
import { KpiCard, KpiCardSkeleton } from '@/features/dashboard/components/KpiCard';
import { EngineerPanel } from './components/EngineerPanel';
import { STAGE_BADGE } from './stage-badge';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { SearchBar } from '@/components/shared/SearchBar';
import { Pagination } from '@/components/shared/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import { SkeletonPage } from '@/components/skeletons';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';
import { ROUTES } from '@/routes/routes';

const PAGE_SIZE = 20;

type Tab = 'unassigned' | 'all-active';

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString();
}

interface ForceTransferTarget {
  id: string;
  inspectionNumber: string;
  mode: ForceTransferDialogMode;
}

export function EngineerAssignmentCenterPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const can = useHasPermission();

  const [tab, setTab] = useState<Tab>('unassigned');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<ForceTransferTarget | null>(null);

  const canAssign = can('inspection:ownership:force-transfer');

  const assignmentQuery = useQuery({
    queryKey: ['assignment-center'],
    queryFn: dashboardService.getAssignmentCenterData,
    staleTime: 30_000,
  });

  const opsDashboardQuery = useQuery({
    queryKey: ['dashboard-operations'],
    queryFn: dashboardService.getOperationsDashboard,
    staleTime: 60_000,
  });

  const departmentsQuery = useQuery({
    queryKey: ['ops-work-queue-departments'],
    queryFn: departmentService.listAll,
    staleTime: 60_000,
  });

  const jobsQuery = useQuery({
    queryKey: ['assignment-queue', tab, page, debouncedSearch],
    queryFn: () =>
      tab === 'unassigned'
        ? inspectionService.getJobPool({
            page,
            pageSize: PAGE_SIZE,
            search: debouncedSearch || undefined,
          })
        : inspectionService.list({
            page,
            pageSize: PAGE_SIZE,
            search: debouncedSearch || undefined,
          }),
  });

  const invalidateAll = () => {
    void qc.invalidateQueries({ queryKey: ['assignment-center'] });
    void qc.invalidateQueries({ queryKey: ['assignment-queue'] });
    void qc.invalidateQueries({ queryKey: ['dashboard-operations'] });
    void qc.invalidateQueries({ queryKey: ['ops-work-queue'] });
  };

  const switchTab = (t: Tab) => {
    setTab(t);
    setPage(1);
    setSelectedJobId(null);
  };

  if (assignmentQuery.isLoading) return <SkeletonPage />;

  const data = jobsQuery.data;
  const selectedJob = data?.data.find((j) => j.id === selectedJobId) ?? null;

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Operations', to: ROUTES.DASHBOARD_OPERATIONS },
          { label: 'Assignment Center' },
        ]}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Engineer Assignment Center</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Assign, reassign, and track engineer workload from one workspace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SearchBar
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search job #, equipment, customer…"
          />
          <button
            type="button"
            onClick={() => {
              void jobsQuery.refetch();
              void assignmentQuery.refetch();
            }}
            className="flex h-10 items-center gap-2 rounded-lg border border-input bg-card px-3 text-sm text-foreground hover:bg-muted"
            title="Refresh"
          >
            <FontAwesomeIcon
              icon={faArrowsRotate}
              className={jobsQuery.isFetching ? 'animate-spin' : ''}
            />
          </button>
        </div>
      </div>

      {assignmentQuery.isError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load assignment data. Check your connection and refresh.
        </div>
      )}

      {/* ── Workload Summary ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {assignmentQuery.data && (
          <>
            <KpiCard
              label="Active Jobs (Team)"
              value={assignmentQuery.data.teamWide.activeJobs}
              icon={faListCheck}
              iconCls="bg-blue-500/10 text-blue-700"
            />
            <KpiCard
              label="Assigned Jobs (Team)"
              value={assignmentQuery.data.teamWide.assignedJobs}
              icon={faUserGear}
              iconCls="bg-amber-500/10 text-amber-700"
            />
          </>
        )}
        {opsDashboardQuery.isLoading ? (
          <>
            <KpiCardSkeleton />
            <KpiCardSkeleton />
          </>
        ) : opsDashboardQuery.data ? (
          <>
            <KpiCard
              label="Jobs Waiting Review"
              value={opsDashboardQuery.data.kpis.pendingOpsReviews}
              icon={faClipboardCheck}
              iconCls="bg-orange-500/10 text-orange-700"
              to={`${ROUTES.INSPECTIONS}?status=COMPLETED&operationsReviewStatus=PENDING`}
            />
            <KpiCard
              label="Jobs Completed Today"
              value={opsDashboardQuery.data.kpis.completedToday}
              icon={faClipboardCheck}
              iconCls="bg-teal-500/10 text-teal-700"
              to={`${ROUTES.INSPECTIONS}?status=COMPLETED`}
            />
          </>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        {/* ── Assignment Queue ────────────────────────────────────────────── */}
        <div className="space-y-3 lg:col-span-3">
          <div className="flex gap-2 border-b border-border">
            <button
              type="button"
              onClick={() => switchTab('unassigned')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                tab === 'unassigned'
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Unassigned
            </button>
            <button
              type="button"
              onClick={() => switchTab('all-active')}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                tab === 'all-active'
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              All Active
            </button>
          </div>

          {jobsQuery.isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading jobs…</p>
          ) : jobsQuery.isError ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              Could not load jobs. Check your connection and refresh.
            </div>
          ) : data && data.total > 0 ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="border-b border-border text-left text-foreground">
                      <th className="px-3 py-2.5 font-medium">Job Number</th>
                      <th className="px-3 py-2.5 font-medium">Customer</th>
                      <th className="px-3 py-2.5 font-medium">Equipment</th>
                      <th className="px-3 py-2.5 font-medium">Priority</th>
                      <th className="px-3 py-2.5 font-medium">Received</th>
                      <th className="px-3 py-2.5 font-medium">Current Stage</th>
                      <th className="px-3 py-2.5 font-medium">Days Waiting</th>
                      <th className="px-3 py-2.5 font-medium">Current Engineer / Owner</th>
                      <th className="px-3 py-2.5 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.data.map((job: Inspection) => (
                      <tr
                        key={job.id}
                        className={`hover:bg-muted/30 ${selectedJobId === job.id ? 'bg-muted/40' : ''}`}
                      >
                        <td className="px-3 py-2.5 font-medium text-primary">
                          {job.inspectionNumber}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground">
                          {job.customerName ?? '—'}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground">{job.equipmentName}</td>
                        <td className="px-3 py-2.5 text-muted-foreground">
                          {job.priorityName ?? '—'}
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground">
                          {fmtDate(job.intakeReceivedAt)}
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STAGE_BADGE[job.currentStage]}`}
                          >
                            {WORK_QUEUE_STAGE_LABELS[job.currentStage]}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-muted-foreground">{job.daysWaiting}d</td>
                        <td className="px-3 py-2.5 text-muted-foreground">
                          {job.assignedEngineerName ?? '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            {canAssign && !job.assignedEngineerId && (
                              <button
                                onClick={() =>
                                  setTransferTarget({
                                    id: job.id,
                                    inspectionNumber: job.inspectionNumber,
                                    mode: 'assign',
                                  })
                                }
                                className="flex h-7 w-7 items-center justify-center rounded-lg text-green-700 transition-colors hover:bg-green-500/10"
                                aria-label="Assign"
                                title="Assign"
                              >
                                <FontAwesomeIcon icon={faUserPlus} className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {canAssign && job.assignedEngineerId && (
                              <>
                                <button
                                  onClick={() =>
                                    setTransferTarget({
                                      id: job.id,
                                      inspectionNumber: job.inspectionNumber,
                                      mode: 'reassign',
                                    })
                                  }
                                  className="flex h-7 w-7 items-center justify-center rounded-lg text-amber-700 transition-colors hover:bg-amber-500/10"
                                  aria-label="Reassign"
                                  title="Reassign"
                                >
                                  <FontAwesomeIcon icon={faUserGear} className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  onClick={() =>
                                    setTransferTarget({
                                      id: job.id,
                                      inspectionNumber: job.inspectionNumber,
                                      mode: 'force-transfer',
                                    })
                                  }
                                  className="flex h-7 w-7 items-center justify-center rounded-lg text-red-700 transition-colors hover:bg-red-500/10"
                                  aria-label="Force Transfer"
                                  title="Force Transfer"
                                >
                                  <FontAwesomeIcon icon={faBolt} className="h-3.5 w-3.5" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() =>
                                setSelectedJobId((prev) => (prev === job.id ? null : job.id))
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              aria-label="View Assignment History"
                              title="View Assignment History"
                            >
                              <FontAwesomeIcon icon={faClockRotateLeft} className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() =>
                                navigate(
                                  ROUTES.OPS_REVIEW_WORKSPACE.replace(':inspectionId', job.id),
                                )
                              }
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              aria-label="Open Review Workspace"
                              title="Open Review Workspace"
                            >
                              <FontAwesomeIcon icon={faEye} className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPageChange={setPage}
              />
            </>
          ) : (
            <EmptyState
              icon={faUserPlus}
              title={tab === 'unassigned' ? 'No unassigned jobs' : 'No jobs found'}
              description={
                tab === 'unassigned'
                  ? 'Every job currently has an engineer assigned.'
                  : 'Try adjusting your search.'
              }
            />
          )}

          {/* ── Selected Job Detail Strip ───────────────────────────────────── */}
          {selectedJob && (
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                  {selectedJob.inspectionNumber} — Current Assignment
                </h3>
                <button
                  onClick={() => setSelectedJobId(null)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Close
                </button>
              </div>
              <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Current Engineer</p>
                  <p className="text-sm text-foreground">
                    {selectedJob.assignedEngineerName ?? '— Unassigned —'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Assigned At</p>
                  <p className="text-sm text-foreground">{fmtDate(selectedJob.assignedAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Assigned By</p>
                  <p className="text-sm text-foreground">{selectedJob.assignedByName ?? '—'}</p>
                </div>
              </div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Assignment / Ownership History
              </p>
              <OwnershipHistoryPanel inspectionId={selectedJob.id} />
            </div>
          )}
        </div>

        {/* ── Engineer Panel ──────────────────────────────────────────────── */}
        <div className="lg:col-span-2">
          <EngineerPanel
            engineers={assignmentQuery.data?.engineers ?? []}
            departments={departmentsQuery.data ?? []}
          />
        </div>
      </div>

      {/* ── Recent Activity ─────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold text-foreground">Recent Assignment Activity</h2>
        </div>
        <div className="divide-y divide-border">
          {(assignmentQuery.data?.recentActivity.length ?? 0) === 0 ? (
            <p className="px-5 py-6 text-sm text-muted-foreground">
              No recent assignment activity.
            </p>
          ) : (
            assignmentQuery.data?.recentActivity.map((item) => (
              <div
                key={`${item.inspectionId}-${item.assignedAt}`}
                className="flex items-center justify-between px-5 py-3 text-sm"
              >
                <div>
                  <span className="font-medium text-primary">{item.inspectionNumber}</span>
                  <span className="ml-2 text-muted-foreground">
                    assigned to {item.engineerName ?? '—'}
                    {item.assignedByName && ` by ${item.assignedByName}`}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{fmtDate(item.assignedAt)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <ForceTransferDialog
        inspectionId={transferTarget?.id ?? ''}
        open={transferTarget !== null}
        mode={transferTarget?.mode}
        onClose={() => {
          setTransferTarget(null);
          invalidateAll();
        }}
      />
    </div>
  );
}
